import { TrainingStatus } from '../shared/constants';

export enum Account {
  SIGNIN = '/account/signin',
  SIGNUP = '/account/signup',
  VERIFY = '/account/verify',
  CHECK_EMAIL = '/account/check-email',
}

export enum Search {
  ROOT = '/search',
  GLOBAL = '/global',
}

/** Outgoing: кого я разрешаю (настройки). Incoming: кто мне дал доступ (тренировки). */
export const UserTrainingAccess = {
  OUTGOING: '/user-training-access/outgoing',
  INCOMING: '/user-training-access/incoming',
};

export enum User {
  ROOT = '/users',
  COMMUNITIES = '/users/${1}/communities',
  SUBSCRIPTIONS = '/users/${1}/subscriptions',
  SUBSCRIBERS = '/users/${1}/subscribers',
  SUBSCRIBER_REQUESTS = '/users/subscriber-requests',
  REJECTED_SUBSCRIBER_REQUESTS = '/users/rejected-subscriber-requests',
  RESTRICTION_PERMISSIONS = '/users/restriction-permissions',
  ME = '/users/me',
  ME_ONBOARDING = '/users/me/onboarding',
  ME_ONBOARDING_BLOCK_COMPLETIONS = '/users/me/onboarding/blocks/${1}/completions',
  ME_ONBOARDING_SKIP_ALL = '/users/me/onboarding/skip-all',
  ME_ONBOARDING_RESET = '/users/me/onboarding/reset',
  ME_DETAILED = '/users/me/detailed',
  ME_CONFIG = '/users/me/config',
  ME_REGISTRATION_COMPLETION = '/users/me/registration-completion',
  ME_MEASUREMENTS = '/users/me/measurements',
  SUBSCRIBE = '/users/${1}/subscriptions',
  UNSUBSCRIBE = '/users/${1}/subscription-cancellations',
  STATUS = '/users/${1}/subscription-status-changes',
}

export const Exercise = {
  ROOT: '/exercises',
  PUBLIC_REQUEST: (id: string) => `/exercises/${id}/public-request`,
  REPLACE_WITH_PUBLIC: (id: string) => `/exercises/${id}/replace-with-public`,
  COPY: (id: string) => `/exercises/${id}/copy`,
  CANCEL_PUBLIC_REQUEST: (id: string) =>
    `/exercises/${id}/cancel-public-request`,
  CREATED_BY_USER: '/exercises/created-by-user',
  ALL_FOR_ADDING: '/exercises/all-for-adding',
  FAVORITE: '/exercises/favorite',
  BY_IDS: '/exercises/by-ids',
  AVAILABLE: '/exercises/available',
  AVAILABLE_DETAILED: '/exercises/available-detailed',
  PUBLIC: '/exercises/public',
  PUBLIC_COMPACT: '/exercises/public-compact',
  ADD: '/add',
  RESULTS: '/exercises/results',
  RESULTS_FOR_USER: (userId: string) => `/exercises/results/${userId}`,
};

export enum ExerciseProperties {
  ROOT = '/exercise-properties',
}

export enum ExerciseOption {
  ROOT = '/exercise-options',
}

export const Program = {
  COLLECTION: '/program-collections',
  COLLECTION_BY_ID: (id: string) => `/program-collections/${id}`,
  SETS: '/program-sets',
  SETS_BY_ID: (id: string) => `/program-sets/${id}`,
  ITEMS: '/program-items',
  ITEMS_BY_ID: (id: string) => `/program-items/${id}`,
  AVAILABLE: `/program-items/available`,
  EXERCISES: (id: string) => `/program-items/${id}/exercises`,
  COPY: (id: string) => `/program-items/${id}/copy`,
  USER: (id: string, userId: string) => `/program-items/${id}/users/${userId}`,
};

export const Training = {
  ROOT: '/trainings',
  AVAILABLE: '/trainings/available',
  CREATED_FOR_OTHERS: '/trainings/created-for-others',
  START: '/trainings/${0}/start',
  START_NEW: '/trainings/start-new',
  EMPTY: '/trainings/empty',
  FINISH: '/trainings/${0}/finish',
  DAY: (day: string, statuses: TrainingStatus[]) =>
    `/trainings/day/${day}${statuses.length > 0 ? `?status=${statuses.map((status) => status.toLowerCase()).join(',')}` : ''}`,
  PERIOD: (period: string, finished = true) =>
    `/trainings/period/${period}${finished ? '?finished=true' : ''}`,
  TRAININGS_FOR_ANALYTIC: '/trainings/trainings-for-analytic',
  SETS: (trainingId: string, setId?: string) =>
    `/trainings/${trainingId}/sets` + (setId ? `/${setId}` : ''),
  SETS_ORDER: (trainingId: string) => `/trainings/${trainingId}/sets/order`,
  SET_ITEM: (trainingId: string, setId: string, itemId?: string) =>
    `/trainings/${trainingId}/sets/${setId}/items` +
    (itemId ? `/${itemId}` : ''),
  ITEM_STATUS: (trainingId: string, setId: string, itemId: string) =>
    `/trainings/${trainingId}/sets/${setId}/items/${itemId}/status`,
  SET_LOAD: (trainingId: string, setId: string) =>
    `/trainings/${trainingId}/sets/${setId}/load`,
  EXERCISES: (id: string) => `/trainings/${id}/exercises`,
  EXERCISES_DETAILED: (id: string) => `/trainings/${id}/exercises-detailed`,
  COPY: (id: string) => `/trainings/${id}/copy`,
  USER: '/trainings/${0}/users/${1}',
  SHARE_USERS: (trainingId: string) => `/trainings/${trainingId}/share-users`,
  AI_TRAINING: '/trainings/ai-training',
  ACTIVITY_TYPES: '/training-activity-types',
};

export enum Language {
  ROOT = '/languages',
}

export enum Translation {
  ROOT = '/translations',
}

export enum Post {
  ROOT = '/posts',
  USER = '/posts/users',
  NEWS = '/posts/feed',
  COMMUNITY = '/posts/communities',
  COMMENT = '/posts/{0}/comments',
  COMMENT_REACTION = '/posts/{0}/comments/{1}/reactions',
  REACTION = '/posts/{0}/reactions',
}

export enum Community {
  ROOT = '/communities',
  SUBSCRIBE = '/communities/${1}/subscriptions',
  UNSUBSCRIBE = '/communities/${1}/subscription-cancellations',
  STATUS = '/communities/${1}/subscription-status-changes',
  MY = '/communities/my',
}

export enum Chat {
  ROOT = '/chats',
  MESSAGE = '/chats/${0}/messages',
  READ_MESSAGES = '/chats/${0}/read-receipts',
  CHAT_TYPING = '/chats/${0}/typing-events',
  UNREAD_MESSAGES = '/chats/unread-messages',
}

export enum Subscriptions {
  ROOT = '/subscriptions',
  PLANS = '/subscriptions/plans',
  RESTRICTIONS = '/subscriptions/restrictions',
}

export enum Payments {
  ROOT = '/payments',
  PLAN = '/plans',
  SUBSCRIPTIONS = '/subscriptions',
  CURRENT = '/current',
  SETUP_INTENT = '/setup-intents',
  DONATIONS_INTENTS = '/donations/intents',
}

export const BugReports = {
  ROOT: '/bug-reports',
  TYPES: '/types',
  REPORT_ID: (id: string) => `/${id}`,
  MESSAGE: (id: string) => `/${id}/messages`,
  STATUS: (id: string) => `/${id}/status`,
};

export const AppConfig = {
  ROOT: '/config',
  VARIABLES: '/variables',
  FEATURES: '/features',
  FORMULAS: '/formulas',
  VERSION: '/version',
  HEALTH: '/health',
  APP_LINK_UPDATE: '/app-link-update',
  FILE_PATHS: '/file-paths',
  VARIABLE_ID: (id: string) => `/variables/${id}`,
};

export const Agreements = {
  ROOT: '/agreements',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  COOKIES: '/cookies',
  ACCEPTANCES: '/acceptances',
};

export const Notifications = {
  READ: '/notifications/read',
  LIST: '/notifications',
  UNREAD: '/notifications/unread',
  UNREAD_COUNT: '/notifications/unread/count',
};

export const Support = {
  ROOT: '/support',
  CONTACT: '/contact',
};
